import React from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@bcc/components/ui/tooltip";
import { useAuth } from "@bcc/contexts/auth-context";
import { Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setRoasterLogoTestId } from "./roaster-logo.utils";

interface RoasterLogoProps {
  testId?: string;
}

export function RoasterLogo({ testId }: RoasterLogoProps) {
  const { roaster } = useAuth();
  const [imageError, setImageError] = React.useState(false);
  const { t } = useTranslation();

  // Show placeholder if no logo or image failed to load
  if (!roaster.roasterLogoUrl || imageError) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              data-testid={testId ?? setRoasterLogoTestId("placeholder")}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-muted"
            >
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">
              {t("layout.roasterLogo.addLogoInSettings")}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid={testId ?? setRoasterLogoTestId("container")}
            className="relative h-16 w-auto max-w-[160px]"
          >
            <img
              data-testid={setRoasterLogoTestId("image")}
              src={roaster.roasterLogoUrl || "/placeholder.svg"}
              alt={`Logo of ${roaster.roasterName}`}
              width={160}
              height={64}
              className="h-16 w-auto object-contain"
              onError={() => {
                console.log("[v0] Logo load failed");
                setImageError(true);
              }}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            {t("layout.roasterLogo.managedBy", {
              roasterName: roaster.roasterName,
            })}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
